import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Seed page state from a URL query param exactly once, after a guard
 * condition becomes truthy.
 *
 * Use case: a page lands at `/detail?metroId=houston-tx`. We need to
 * wait for the metros list to load before we can validate the URL value
 * and select the corresponding metro. After the initial seed, the URL
 * should NOT re-stomp the user's interactive picks -- even if the user
 * navigates to another route and back to the same URL within the SPA
 * session.
 *
 * @param paramName    Query-string key to read (e.g. 'metroId', 'metroIds').
 * @param isReady      Guard -- onSeed fires only after this turns true
 *                     (typically metros.length > 0).
 * @param onSeed       Callback invoked once with the URL value (null if
 *                     the param isn't present). Caller does validation
 *                     + state setting.
 *
 * Notes:
 * - Seeding is one-shot per mount: a useRef tracks whether onSeed has
 *   already fired, so subsequent URL changes within the same mount are
 *   ignored. Pages that need to react to URL changes (e.g. browser
 *   back/forward) should manage that separately.
 * - `onSeed` is referenced via a ref internally to avoid re-running
 *   the effect on every render when callers pass a non-memoized
 *   callback -- important since the seeding fires exactly once and
 *   the callback identity shouldn't matter.
 *
 * See DEC-061 for the consolidation rationale (PR #62 code-reviewer
 * warning #4: 3 different URL-param-seeding patterns across 4 pages).
 */
export function useUrlParamSeed(
  paramName: string,
  isReady: boolean,
  onSeed: (value: string | null) => void,
): void {
  const [searchParams] = useSearchParams();
  const hasSeededRef = useRef(false);
  const onSeedRef = useRef(onSeed);
  onSeedRef.current = onSeed;

  useEffect(() => {
    if (hasSeededRef.current || !isReady) return;
    const value = searchParams.get(paramName);
    onSeedRef.current(value);
    hasSeededRef.current = true;
  }, [paramName, isReady, searchParams]);
}
